import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Styles/ChatOverview.css';

const ChatOverview = () => {
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');

    const [viewMode, setViewMode] = useState('my'); // 'my' or 'unanswered'

    const ws = useRef(null);
    const url = process.env.REACT_APP_API_URL;
    const ws_url = process.env.REACT_APP_WS_URL;

    useEffect(() => {
        // runs when effect starts
        fetchConversations();
        return () => {
            // runs after component unmounts or dependencies change
            if (ws.current) {
                ws.current.close();
            }
        };
    }, []);

    const getCurrentUserId = () => {
        const user = JSON.parse(localStorage.getItem('user'));
        return user ? user.user_id : null;
    };

    const getCurrentGMFId = () => {
        const user = JSON.parse(localStorage.getItem('user'));
        return user ? user.id : null;
    };

    const getCurrentUserName = () => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user) {
            return `${user.first_name} ${user.last_name}`;
        }
        return 'Unknown user';
    };

    const fetchConversations = async () => {
        try {
            const token = localStorage.getItem('token');
            const user = JSON.parse(localStorage.getItem('user'));

            const response = await fetch(`${url}/chat/list`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setConversations(data);
            } else {
                // handle response error
                console.error('Failed trying to fetch conversations');
            }
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }
    };

    const setupWebSocket = (chatId) => {
        // Close existing WebSocket connection if one's open
        if (ws.current) {
            ws.current.close();
        }

        // Open new WebSocket connection for selected conversation
        const token = localStorage.getItem('token');
        ws.current = new WebSocket(
            `${ws_url}/chat/ws/${chatId}?token=${token}`
        );
        ws.current.onmessage = (event) => {
            const data = event.data;
            const messageObject = JSON.parse(data);

            const newMessage = {
                chat_id: chatId,
                chat_message_type:
                    messageObject.chat_message_type || 'gmf_chat',
                timestamp: messageObject.timestamp,
                message: messageObject.message,
                sender_user_id: messageObject.sender_user_id,
                sender_name: messageObject.sender_name,
                read_ids: messageObject.read_ids,
                hcw_read: messageObject.hcw_read || true,
            };

            setMessages((prevMessages) => [...prevMessages, newMessage]);
        };
        ws.current.onerror = (error) => {
            console.error('WebSocket error', error);
        };
        ws.current.onclose = () => {
            console.log('WebSocket connection closed');
        };
    };

    const selectConversation = (conversation) => {
        // Check if the current GMF is in the gmf_ids
        const currentUserId = getCurrentUserId();
        const isGMFInChat = conversation.gmf_ids.includes(currentUserId);

        if (!isGMFInChat) {
            const join = window.confirm(
                'You are not part of this conversation. Would you like to join?'
            );
            if (join) {
                setSelectedConversation(conversation);
                fetchMessages(conversation.id);
                joinConversation(conversation.id);
            }
            return;
        } else {
            setSelectedConversation(conversation);
            fetchMessages(conversation.id);
        }

        // Proceed with setting up WebSocket connection if the GMF is in the chat
        setupWebSocket(conversation.id);
    };

    const joinConversation = async (chatId) => {
        try {
            const token = localStorage.getItem('token');
            const userId = getCurrentGMFId();

            const response = await fetch(
                `${url}/chat/add-gmf/${chatId}`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            if (response.ok) {
                // Successfully joined the chat room
                // Update the conversation list to reflect the change
                fetchConversations();
            } else {
                // Handle error response
                console.error('Failed to join conversation');
            }
        } catch (error) {
            console.error('Error joining conversation:', error);
        }
    };

    const fetchMessages = async (chatId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${url}/chat/messages/${chatId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                setMessages(data);
            } else {
                // handle response error
                console.error('Error');
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    const handleSendMessage = () => {
        // if user input is empty, exit
        if (!messageInput.trim()) return;

        // Send message via WS
        ws.current.send(messageInput);

        const messageData = {
            chat_id: selectedConversation.id,
            chat_message_type: 'gmf_chat',
            timestamp: new Date().toISOString(),
            message: messageInput,
            sender_user_id: getCurrentUserId(),
            sender_name: getCurrentUserName(),
        };

        setMessages((prevMessages) => [...prevMessages, messageData]);
        setMessageInput('');
    };

    // toggles between 'my' and 'unanswered'
    const toggleViewMode = () => {
        setViewMode((prevMode) => (prevMode === 'my' ? 'unanswered' : 'my'));

        // Clear right conversation panel
        setSelectedConversation(null);
        setMessages([]);
    };

    // logic to filter conversations/chats based on viewMode
    const filteredConversations = conversations.filter((conv) => {
        if (viewMode === 'my') {
            return conv.gmf_ids.includes(getCurrentUserId());
        } else {
            return conv.gmf_ids.length === 0;
        }
    });
    return (
        <div className="container">
            <div className="sidebar">
                <div className="conversationHeader">
                    <h2>
                        {viewMode === 'my'
                            ? 'My Conversations'
                            : 'Unanswered Conversations'}
                    </h2>
                    <button onClick={toggleViewMode} className="toggleButton">
                        🔄
                    </button>
                </div>

                <ul className="conversationList">
                    {filteredConversations.map((conv) => (
                        <li
                            key={conv.id}
                            className="conversationItem"
                            onClick={() => selectConversation(conv)}
                        >
                            {conv.name || `Conversation #${conv.id}`}
                            {conv.gmf_ids.length === 0 && (
                                <button
                                    className="joinButton"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        joinConversation(conv.id);
                                    }}
                                >
                                    Join
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="chatWindow">
                {selectedConversation ? (
                    <>
                        <div className="chatHeader">
                            <h2>
                                {selectedConversation.name ||
                                    `Conversation ${selectedConversation.id}`}
                            </h2>
                        </div>
                        <div className="chatMessages">
                            {messages.map((msg, index) => (
                                <div
                                    key={index}
                                    className={`message ${
                                        msg.sender_user_id ===
                                        getCurrentUserId()
                                            ? 'myMessage'
                                            : 'otherMessage'
                                    }`}
                                >
                                    <div>{msg.message}</div>
                                    <small>
                                        {new Date(
                                            msg.timestamp
                                        ).toLocaleTimeString()}{' '}
                                        - {msg.sender_name}
                                    </small>
                                </div>
                            ))}
                        </div>
                        <div className="chatInput">
                            <input
                                type="text"
                                value={messageInput}
                                onChange={(e) =>
                                    setMessageInput(e.target.value)
                                }
                                className="input"
                                placeholder="Type a message..."
                            />
                            <button
                                onClick={handleSendMessage}
                                className="sendButton"
                            >
                                Send
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="noConversationSelected">
                        <p>Select a conversation to start chatting</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatOverview;
